import React, { useState } from 'react';
import { useTranslation } from "react-i18next";
import { Link } from 'react-router-dom';
import { useForm } from "react-hook-form";

import { CactusLabelInput } from "../../components";
import useFormValidatorEmail from '../../hooks/validators/use-form-validator-email';
import useFormValidatorText from '../../hooks/validators/use-form-validator-text';

export type LoginInputs = {
    email: string;
    password: string;
}

interface LoginFormProps {
    onSubmit: (data: LoginInputs) => void;
    btnLoading?: boolean;
}

const IconEyeOpen = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
        <circle cx="12" cy="12" r="3" />
    </svg>
);

const IconEyeClosed = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94" />
        <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19" />
        <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
);

const LoginForm: React.FC<LoginFormProps> = (props: LoginFormProps) => {

    const [t] = useTranslation();
    const { handleSubmit, register, errors } = useForm<LoginInputs>();
    const [showPassword, setShowPassword] = useState(false);

    const emailRegister = register(useFormValidatorEmail());
    const passwordRegister = register(useFormValidatorText());

    return(
        <form onSubmit={handleSubmit(props.onSubmit)}>

            <CactusLabelInput
                labelTitle={t('Email')}
                inputAttributes={{
                    "data-testid": "email",
                    id: "email",
                    name: "email",
                    type:  "email",
                    placeholder: "*****@email.com",
                    autoComplete: "email",
                    ref: emailRegister,
                }}
                errorMessage={errors.email ? errors.email.message : null}
            />

            <div className="mt-2">
                <label className="w-100">
                    <span className="form__label">{t('Password')}</span>
                    <div className="auth__input-wrapper mt-2">
                        <input
                            data-testid="password"
                            id="password"
                            name="password"
                            type={showPassword ? "text" : "password"}
                            placeholder="*****"
                            autoComplete="current-password"
                            ref={passwordRegister}
                            className="w-100 form-control--dark"
                        />
                        <button
                            type="button"
                            className="auth__password-toggle"
                            onClick={() => setShowPassword(v => !v)}
                            tabIndex={-1}
                            aria-label={showPassword ? t('hide_password') : t('show_password')}
                        >
                            {showPassword ? <IconEyeClosed /> : <IconEyeOpen />}
                        </button>
                    </div>
                    {errors.password && (
                        <div className="mt-2 form__invalid-feedback font-weight-bold">
                            {errors.password.message}
                        </div>
                    )}
                </label>
            </div>

            <div className="d-flex justify-content-end mt-1">
                <Link to="/forgot-password" className="auth__forgot-link">{t('forgot_password')}</Link>
            </div>

            <div className="d-flex justify-content-end">
                <button
                    data-testid="submit"
                    type="submit"
                    className="btn btn-cactus mt-2"
                    disabled={props.btnLoading ?? false}
                >
                    {props.btnLoading
                        ? <span className="auth__btn-spinner" />
                        : t('Login')
                    }
                </button>
            </div>

        </form>
    );

};

export default React.memo(LoginForm);
